import { create } from "zustand";
import {
  isConsentExpired,
  isConsentVersionOutdated,
  loadConsent,
  resetConsent,
  saveConsent,
} from "@/lib/cookie-consent-manager";
import {
  CONSENT_VERSION,
  type CookieCategory,
  type CookieConsent,
  DEFAULT_CONSENT,
} from "@/types/cookie-consent";

interface CookieConsentState {
  // 状態
  consent: CookieConsent | null;
  hasConsented: boolean;
  showBanner: boolean;
  showPreferences: boolean;
  isLoading: boolean;

  // Actions
  initialize: () => void;
  setConsent: (consent: Partial<CookieConsent>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  updateCategory: (category: CookieCategory, enabled: boolean) => void;
  openBanner: () => void;
  closeBanner: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  reset: () => void;
  checkExpiry: () => boolean;
}

export const useCookieConsent = create<CookieConsentState>((set, get) => ({
  // 初期状態
  consent: null,
  hasConsented: false,
  showBanner: false,
  showPreferences: false,
  isLoading: true,

  // 初期化（アプリ起動時に呼び出し）
  initialize: () => {
    const savedConsent = loadConsent();

    if (savedConsent) {
      // 有効期限チェック
      if (isConsentExpired(savedConsent)) {
        set({ showBanner: true, hasConsented: false, isLoading: false });
        return;
      }

      // バージョンチェック
      if (isConsentVersionOutdated(savedConsent, CONSENT_VERSION)) {
        set({ showBanner: true, hasConsented: false, isLoading: false });
        return;
      }

      // 有効な同意が存在
      set({
        consent: savedConsent,
        hasConsented: true,
        showBanner: false,
        isLoading: false,
      });
    } else {
      // 初回訪問
      set({ showBanner: true, hasConsented: false, isLoading: false });
    }
  },

  // 同意設定
  setConsent: (partialConsent) => {
    const currentConsent = get().consent || DEFAULT_CONSENT;
    const newConsent: CookieConsent = {
      ...currentConsent,
      ...partialConsent,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
      expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
    };

    saveConsent(newConsent);
    set({
      consent: newConsent,
      hasConsented: true,
      showBanner: false,
      showPreferences: false,
    });
  },

  // 全て許可
  acceptAll: () => {
    get().setConsent({
      essential: true,
      functional: true,
      analytics: true,
      advertising: true,
    });
  },

  // 全て拒否（必須のみ許可）
  rejectAll: () => {
    get().setConsent({
      essential: true,
      functional: false,
      analytics: false,
      advertising: false,
    });
  },

  // カテゴリ別更新
  updateCategory: (category, enabled) => {
    // 必須Cookieは変更不可
    if (category === "essential") return;

    get().setConsent({
      [category]: enabled,
    });
  },

  // バナー表示
  openBanner: () => set({ showBanner: true }),
  closeBanner: () => set({ showBanner: false }),

  // 詳細設定モーダル
  openPreferences: () => set({ showPreferences: true }),
  closePreferences: () => set({ showPreferences: false }),

  // リセット
  reset: () => {
    resetConsent();
    set({
      consent: null,
      hasConsented: false,
      showBanner: true,
      showPreferences: false,
    });
  },

  // 有効期限チェック
  checkExpiry: () => {
    const currentConsent = get().consent;
    if (!currentConsent) return false;

    const expired = isConsentExpired(currentConsent);
    if (expired) {
      get().reset();
    }
    return expired;
  },
}));
